import { useMemo, useState } from "react";
import { Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../Logo";
import { createEnrollment } from "../services/api";
import { getCourseById } from "../constants/courseCatalog";
import "./Checkout.css";

function Checkout() {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedCourseId = searchParams.get("course");

  const [form, setForm] = useState({
    name: userData?.name || user?.displayName || user?.name || "",
    email: user?.email || "",
    phone: userData?.phone || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);

  const selectedCourse = useMemo(
    () => (requestedCourseId ? getCourseById(requestedCourseId) : null),
    [requestedCourseId]
  );

  if (!requestedCourseId || !selectedCourse) {
    return <Navigate to="/pricing" replace />;
  }

  const subtotal = selectedCourse.price;
  const supportFee = 0;
  const totalAmountPaid = subtotal + supportFee;

  const displayName =
    userData?.name ||
    user?.displayName ||
    user?.name ||
    user?.email?.split("@")[0] ||
    "Codeion learner";

  const userId = userData?.userID || user?.userID || "User ID pending";

  // 🔥 Load Razorpay SDK
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // 🔥 PAYMENT FUNCTION
  const handlePayment = async () => {
    // ✅ Validate form before payment
    if (!form.name.trim() || !form.email.trim() || form.phone.length < 10) {
      setError("Please complete all fields with valid details.");
      return;
    }

    setLoading(true);
    setError("");

    const res = await loadRazorpay();

    if (!res) {
      setError("Payment SDK failed to load.");
      setLoading(false);
      return;
    }

    try {
  // 1️⃣ Create order from backend
  const response = await fetch("/api/create-order", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ amount: totalAmountPaid }),
  });

  if (!response.ok) {
    throw new Error("Failed to create order");
  }

const order = await response.json();

      // 2️⃣ Razorpay options
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        name: "CODEION",
        description: selectedCourse.name,
        order_id: order.id,

        handler: async function (response) {
  try {

    // ✅ Verify payment
    const verifyResponse = await fetch(
      "/api/verify-payment",
      {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(response),
      }
    );

    if (!verifyResponse.ok) {
      throw new Error(
        "Payment verification failed"
      );
    }

   

    // ✅ Save local enrollment
    // ✅ Generate enrollment ID
const enrollmentID = `ENR-${Date.now()}`;

// ✅ Save local enrollment
await createEnrollment({
      enrollmentID,

      userID: userId,

      uid:
        user?.id ||
        user?.email ||
        `local-${Date.now()}`,

      name:
        form.name.trim(),

      email:
        form.email.trim(),

      phone:
        form.phone,

      course:
        selectedCourse.name,

      courseID:
        selectedCourse.id,

      amountPaid:
        totalAmountPaid,

      paymentId:
        response.razorpay_payment_id,
    });

    // ✅ Success page
    setSuccessData({
      enrollmentID,

      userID: userId,

      name:
        form.name.trim(),

      email:
        form.email.trim(),

      phone:
        form.phone,

      course:
        selectedCourse.name,

      amountPaid:
        totalAmountPaid,
    });

  } catch (err) {
    console.error(err);

    setError(
      err.message ||
      "Payment verification failed."
    );
  }
},
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },

        theme: {
          color: "#f5a623",
        },
      };

      const paymentObject = new window.Razorpay(options);

document.body.classList.add("razorpay-open");

paymentObject.open();

paymentObject.on("payment.failed", function () {
  document.body.classList.remove("razorpay-open");
});

    } catch (err) {
      setError("Payment failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    const nextValue =
      name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value;

    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setError("");
  };

  // ✅ SUCCESS PAGE
  if (successData) {
    return (
      <div className="checkout-page">
        <div className="checkout-success-shell">
          <Logo size="small" />
          <div className="checkout-success-badge">
            Enrollment successful 🎉
          </div>

          <h1>Your Codeion enrollment is confirmed.</h1>

          <div className="checkout-success-card">
            <div className="checkout-success-row">
              <span>Enrollment ID</span>
              <strong>{successData.enrollmentID}</strong>
            </div>
            <div className="checkout-success-row">
              <span>User ID</span>
              <strong>{successData.userID}</strong>
            </div>
            <div className="checkout-success-row">
              <span>Name</span>
              <strong>{successData.name}</strong>
            </div>
            <div className="checkout-success-row">
              <span>Email</span>
              <strong>{successData.email}</strong>
            </div>
            <div className="checkout-success-row">
              <span>Phone</span>
              <strong>{successData.phone}</strong>
            </div>
            <div className="checkout-success-row">
              <span>Course</span>
              <strong>{successData.course}</strong>
            </div>
            <div className="checkout-success-row">
              <span>Total Paid</span>
              <strong>Rs {successData.amountPaid}</strong>
            </div>
          </div>

          <button
            className="checkout-btn"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ✅ MAIN UI
  return (
    <div className="checkout-page">
      <div className="checkout-shell">
        <header className="checkout-header">
  <Link to="/" className="checkout-logo-link">
    <Logo size="small" />
  </Link>

  <span className="checkout-kicker">Secure checkout</span>
</header>

        <div className="checkout-layout">
          <section className="checkout-left">
            <h2>{selectedCourse.name}</h2>
            <p>{selectedCourse.description}</p>
            <h3>Rs {totalAmountPaid}</h3>
          </section>

          <section className="checkout-right">
            {error && <div className="checkout-error">{error}</div>}

            <div className="form-group">
              <input
                name="name"
                placeholder="Full Name"
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <input
                name="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <input
                name="phone"
                placeholder="Phone"
                value={form.phone}
                onChange={handleChange}
              />
            </div>

            <button
              className="checkout-btn"
              onClick={handlePayment}
              disabled={loading}
            >
              {loading
                ? "Processing..."
                : `Pay Rs ${totalAmountPaid}`}
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Checkout;